import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Text } from 'components/core/Text';
import { H3 } from 'components/core/Typography';
import { Image } from 'components/core/Image';
import { Button } from 'components/core/Button';
import { waitForFonts } from 'utils/fonts';

import './unitListV2.scss';
import UnitDetails from '../UnitDetails';
import ClosedSignIcon from 'assets/images/icons/closed-sign-tilt.svg';
import ErrorIcon from 'assets/images/error/error.svg';

const MOBILE_BREAKPOINT = 575;

export function UnitListV2({
    children,
    className,
    facility,
    phone,
    units = [],
    unitDetailsComponentProps = () => { },
    unitDetailsComponent = UnitDetails,
    isVipUnit,
    showIcons
}) {
    const [viewType, setViewType] = useState('grid'); // 'grid' or 'list'
    const gridRef = useRef(null);
    const rowHeightsRef = useRef(new Map());
    const rafRef = useRef(null);

    /**
     * Lock row heights ONCE
     */
    const lockRowHeights = useCallback(() => {
        const grid = gridRef.current;
        if (!grid) return;
        if (window.innerWidth < MOBILE_BREAKPOINT) return;

        const cards = Array.from(
        grid.querySelectorAll('.bookable-unit-details-content-v2')
        );
        if (!cards.length) return;

        const rows = new Map();

        cards.forEach(card => {
        const top = card.offsetTop;
        if (!rows.has(top)) rows.set(top, []);
        rows.get(top).push(card);
        });

        rows.forEach((rowCards, top) => {
        if (rowHeightsRef.current.has(top)) return;

        const maxHeight = Math.max(
            ...rowCards.map(card => card.offsetHeight)
        );

        rowHeightsRef.current.set(top, maxHeight);

        rowCards.forEach(card => {
            if (!card.classList.contains('amenities-expanded')) {
            card.style.minHeight = `${maxHeight}px`;
            }
        });
        });
    }, []);

    useEffect(() => {
        const grid = gridRef.current;
        if (!grid) return;

        let isCancelled = false;

        rowHeightsRef.current.clear();
        cancelAnimationFrame(rafRef.current);
        
        const cards = grid.querySelectorAll('.bookable-unit-details-content-v2');
        cards.forEach(card => (card.style.minHeight = ''));

        if (window.innerWidth < MOBILE_BREAKPOINT) return;

        const measureLayout = async () => {
            // Wait for fonts first
            await waitForFonts();
            
            // Check if effect was cancelled while waiting for fonts
            if (isCancelled) return;
            
            let lastHeight = 0;
            let stableCount = 0;
            let attempts = 0;
            const MAX_ATTEMPTS = 30;

            const checkStability = () => {
                // Check if effect was cancelled
                if (isCancelled) return;
                
                const currentHeight = grid.offsetHeight;
                
                if (currentHeight === 0 && attempts < MAX_ATTEMPTS) {
                    attempts++;
                    rafRef.current = requestAnimationFrame(checkStability);
                    return;
                }

                if (currentHeight === lastHeight && currentHeight > 0) {
                    stableCount += 1;
                } else {
                    stableCount = 0;
                    lastHeight = currentHeight;
                }

                // 3 stable frames = lock heights
                if (stableCount >= 3) {
                    lockRowHeights();
                    return;
                }

                if (attempts < MAX_ATTEMPTS) {
                    attempts++;
                    rafRef.current = requestAnimationFrame(checkStability);
                } else {
                    lockRowHeights(); // Fallback
                }
            };

            rafRef.current = requestAnimationFrame(checkStability);
        };

        measureLayout();

        return () => {
            isCancelled = true;
            cancelAnimationFrame(rafRef.current);
        };
    }, [units, facility?.id, lockRowHeights]);

    useEffect(() => {
        const grid = gridRef.current;
        if (!grid) return;

        let resizeTimeout;
        let lastWidth = window.innerWidth;

        const handleResize = () => {
            const newWidth = window.innerWidth;
            
            // Ignore small changes (< 10px) to reduce unnecessary recalcs
            if (Math.abs(newWidth - lastWidth) < 10) return;
            
            const isMobile = newWidth < MOBILE_BREAKPOINT;
            
            clearTimeout(resizeTimeout);
            
            if (isMobile) {
                // Clear heights on mobile
                rowHeightsRef.current.clear();
                const cards = grid.querySelectorAll('.bookable-unit-details-content-v2');
                cards.forEach(card => (card.style.minHeight = ''));
                lastWidth = newWidth;
                return;
            }
            
            lastWidth = newWidth;
            
            // Debounced recalculation for desktop
            resizeTimeout = setTimeout(() => {
                rowHeightsRef.current.clear();
                const cards = grid.querySelectorAll('.bookable-unit-details-content-v2');
                cards.forEach(card => (card.style.minHeight = ''));
                lockRowHeights();
            }, 250);
        };

        // Listen to window resize events
        window.addEventListener('resize', handleResize);
        
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(resizeTimeout);
        };
    }, [lockRowHeights]);

    if (!units.length) {
        return (
            <div className="no-units-found">
                <Image src={ErrorIcon} alt="No Units Found" className="error-icon" />
                <Image src={ClosedSignIcon} alt="No Units Found" className="closed-sign-icon" />
                <H3 className="no-units-title" color="black">No results found</H3>
                <Text className="no-units-subtitle">
                    Please expand your filter selections to see more units
                </Text>
            </div>
        )
    }
    return (
        <div className={classnames(className, 'unit-list-container')}>
            {/* <div className="view-toggle-row">
                <div className="view-toggle">
                    <Button {...(viewType === 'grid' ? { white: true } : { gray: true })} onClick={() => setViewType('grid')}>
                        <Image src={GridIcon} alt="Grid View" />
                    </Button>
                    <Button {...(viewType === 'list' ? { white: true } : { gray: true })} onClick={() => setViewType('list')}>
                        <Image src={ListIcon} alt="List View" />
                    </Button>
                </div>
            </div> */}

            <div ref={gridRef} className={classnames('unit-cards-grid grid-view', {
                vip: isVipUnit,
                // 'list-view': viewType === 'list',
                // 'grid-view': viewType === 'grid'
            })}>
                {units.map((unit) => {
                    const props = {
                        ...unitDetailsComponentProps(unit),
                        unit,
                        facility,
                        showIcon: showIcons,
                        phone,
                        key: unit.id,
                    };
                    return React.createElement(unitDetailsComponent, props);
                })}
                {children}
            </div>
        </div>
    );
}

UnitListV2.propTypes = {
    /** Immutable list of units */
    units: PropTypes.arrayOf(PropTypes.object),
    facility: PropTypes.object.isRequired,
    /** The component to render the unit with */
    unitDetailsComponent: PropTypes.func,
    unitDetailsComponentProps: PropTypes.func,
    className: PropTypes.string,
    children: PropTypes.node,
    phone: PropTypes.object,
    isVipUnit: PropTypes.bool,
    showIcons: PropTypes.bool
};

export default UnitListV2;